import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReauth } from 'utils/services';

import { Survey } from 'models/survey.types';

import {
  CheckSurveysEligibilityReqProps,
  GetSurveyDetailsReqProps,
  GetSurveyDetailsResProps,
  GetSurveyPrescriptionsReqProps,
  GetSurveyPrescriptionsResProps,
  GetSurveysResProps,
  SelectSurveyPrescriptionsReqProps,
  SelectSurveyPrescriptionsResProps,
  SelectSurveyPrescriptionsResult,
  SubmitSurveyResponseReqProps,
  SubmitSurveyResponseResProps,
  SurveyPrescription,
  SurveysMoodCheckInResProps
} from './surveys.types';

export const surveysApi = createApi({
  baseQuery: baseQueryWithReauth,
  endpoints: (build) => ({
    checkSurveysEligibility: build.mutation<unknown, CheckSurveysEligibilityReqProps>({
      query: ({ orderId, surveyId, surveyType }) => ({
        body: surveyId ? { surveyId } : { orderId },
        method: 'POST',
        url: `/surveys/${surveyType}/eligibility`
      })
    }),
    getSurveyDetails: build.query<GetSurveyDetailsResProps, GetSurveyDetailsReqProps>({
      keepUnusedDataFor: 0,
      query: ({ surveyId }) => ({
        url: `/surveys/${surveyId}/details`
      })
    }),
    getSurveyPrescriptions: build.query<
      { defaultValue: string[]; prescriptions: SurveyPrescription[] },
      GetSurveyPrescriptionsReqProps
    >({
      keepUnusedDataFor: 0,
      query: ({ surveyId }) => ({
        url: `/surveys/${surveyId}/prescriptions`
      }),
      transformResponse: (response: GetSurveyPrescriptionsResProps) => response.data
    }),
    getSurveys: build.query<Survey[], void>({
      query: () => ({
        url: '/surveys'
      }),
      transformResponse: (response: GetSurveysResProps) => response.data
    }),
    getSurveysMoodCheckIn: build.query<SurveysMoodCheckInResProps, void>({
      query: () => ({
        url: '/surveys/mood-check-in'
      })
    }),
    selectSurveyPrescriptions: build.mutation<
      SelectSurveyPrescriptionsResult,
      SelectSurveyPrescriptionsReqProps
    >({
      query: ({ selectedPrescriptionIds, surveyId, unselectedPrescriptionIds }) => ({
        body: { selectedPrescriptionIds, unselectedPrescriptionIds },
        method: 'POST',
        url: `/surveys/${surveyId}/selected-prescriptions`
      }),
      transformResponse: (response: SelectSurveyPrescriptionsResProps) => response.data
    }),
    submitSurveyResponse: build.mutation<
      SubmitSurveyResponseResProps,
      SubmitSurveyResponseReqProps
    >({
      query: ({ response, surveyId }) => ({
        body: { response },
        method: 'POST',
        url: `/surveys/${surveyId}/response`
      })
    })
  }),
  reducerPath: 'surveysApi',
  tagTypes: ['Surveys']
});

export const {
  useCheckSurveysEligibilityMutation,
  useGetSurveyDetailsQuery,
  useGetSurveyPrescriptionsQuery,
  useGetSurveysQuery,
  useLazyGetSurveysMoodCheckInQuery,
  useLazyGetSurveysQuery,
  useSelectSurveyPrescriptionsMutation,
  useSubmitSurveyResponseMutation
} = surveysApi;
